import React, { useEffect, useState } from "react";
import { X, Calendar, Brain, FileText, MessageSquare, Mic, Globe, PhoneForwarded } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import ApiService from "../../services/Api.service";
import AuthService from "../../services/Auth.service";
import ToggleSwitch from "../ToggelSwitch/ToggelSwitch";

const AddCampaignModal = ({ isOpen, onClose, companyId, campaignToEdit, onSave }) => {
    const [ivrDropdowns, setIvrDropdowns] = useState({
        stt_options: [],
        llm_options: [],
        tts_options: [],
        script_options: [],
        dialer_options: [],
    });

    const [statusOptions, setStatusOptions] = useState([]);
    const [callNumbersOptions, setCallNumbersOptions] = useState({});
    const [filterValue, setFilterValue] = useState("");
    const [availableNumbers, setAvailableNumbers] = useState([]);
    const [languageMode, setLanguageMode] = useState("default"); // default | other
    const [selectedLanguage, setSelectedLanguage] = useState("");
    const [languageOptions, setLanguageOptions] = useState({})
    const [dispositionOptions, setDispositionOptions] = useState([])


    const {
        handleSubmit,
        control,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            campaign_name: "",
            campaign_description: "",
            start_time: "",
            end_time: "",
            status: "draft",
            script: "",
            dialer: "",
            numbers: [],
            stt_type: "",
            llm_type: "",
            tts_type: "",
            timeout: 10,
            language_mode: "default",
            language: "",
            dispositions:[],
            call_transfer: false,
            call_transfer_number: "",
            call_transfer_threshold: 0,
            service_id: "",
        },
    });


    const fetchDropdownOptionsDetail = async () => {
        try {
            let campaignDataRes;
            let statusRes;
            let numbersRes;

            const promises = [
                ApiService.getCampaignStatusOptions(),
                AuthService.getCallNumberOptions(),
            ];

            // Only fetch campaign details when editing
            if (campaignToEdit?.campaign_id) {
                promises.unshift(AuthService.getCampaignById(campaignToEdit?.campaign_id));
            }

            const results = await Promise.all(promises);

            // Destructure results depending on edit or add mode
            if (campaignToEdit?.campaign_id) {
                [campaignDataRes, statusRes, numbersRes] = results;
            } else {
                [statusRes, numbersRes] = results;
            }
            if (statusRes?.status === 200 && statusRes.data?.data) setStatusOptions(statusRes.data.data);
            if (numbersRes?.status === 200 && numbersRes.data) {
                setCallNumbersOptions(numbersRes.data || {});
            } else {
                setCallNumbersOptions({});
            }

            // --- When editing, populate campaign details ---
            if (campaignToEdit?.campaign_id && campaignDataRes?.status === 200) {
                const c = campaignDataRes.data;

                // Convert seconds to HH:mm
                const secondsToTime = (s) => {
                    const h = Math.floor(s / 3600);
                    const m = Math.floor((s % 3600) / 60);
                    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                };

                reset({
                    campaign_name: c.campaign_name || "",
                    campaign_description: c.campaign_description || "",
                    start_time: secondsToTime(c.start_time || 0),
                    end_time: secondsToTime(c.end_time || 0),
                    status: c.status || "draft",
                    script: c.script || "",
                    dialer: c.dialer || "",
                    numbers: c.numbers || [],
                    timeout: c.timeout || 0,
                    stt_type: c.stt_type || "",
                    tts_type: c.tts_type || "",
                    llm_type: c.llm_type || "",
                    language_mode: c.language_mode || "default",
                    language: c.language || "",
                    dispositions:c.dispositions ||[],
                    call_transfer: Boolean(c.call_transfer),
                    call_transfer_number: c.call_transfer_number || "",
                    call_transfer_threshold: c.call_transfer_threshold ?? 0,
                    service_id: c.service_id || "",
                });

                setFilterValue(c.dialer || "");
            }
        } catch (error) {
            console.error("Error fetching dropdown options:", error);
            toast.error("Failed to load configuration options");
        }
    };
    // Fetch dropdown data from backend
    const fetchDropdownDetail = async (selectedLanguage) => {
        try {
            const payload = {
                language: selectedLanguage
            }
            const ivrOptionsRes = await ApiService.getIvrOptions(payload);
            const ivrDispo = await ApiService.getDispositionListOptions(payload);

            if (ivrDispo.status === 200) {
                setDispositionOptions(ivrDispo.data.dispositions);
            }
            if (ivrOptionsRes.status === 200) {
                setIvrDropdowns(ivrOptionsRes.data);
            }
        } catch (error) {
            console.error("Error fetching IVR options:", error);
            toast.error("Failed to load IVR configuration options");
        }
    };
    // Fetch dropdown data from backend
    const fetchLanguageOptions = async () => {
        try {
            const languageRes = await ApiService.getLanguageOptions();
            if (languageRes.status === 200) {
                setLanguageOptions(languageRes.data);

                const defaultLang =
                    Array.isArray(languageRes.data?.default_language) && languageRes.data.default_language.length > 0
                        ? languageRes.data.default_language[0].value
                        : "";
                setSelectedLanguage(defaultLang);  // This will trigger your useEffect that fetches dropdown
            }
        } catch (error) {
            toast.error("Failed to load IVR configuration options");
        }
    };
    


    useEffect(() => {
        if (isOpen) fetchDropdownOptionsDetail();
    }, [isOpen]);

    useEffect(() => {
        fetchLanguageOptions()
    }, []);

    useEffect(() => {
        if (selectedLanguage) {
            fetchDropdownDetail(selectedLanguage);
        }
    }, [selectedLanguage]);



    useEffect(() => {
        if (campaignToEdit) {
            reset({
                campaign_name: campaignToEdit.campaign_name || "",
                campaign_description: campaignToEdit.campaign_description || "",
                start_time: campaignToEdit.start_time || "",
                end_time: campaignToEdit.end_time || "",
                status: campaignToEdit.status || "draft",
                script: campaignToEdit.script || "",
                dialer: campaignToEdit.dialer || "",
                numbers: campaignToEdit.numbers || [],
                stt_type: campaignToEdit.stt_type || "",
                llm_type: campaignToEdit.llm_type || "",
                tts_type: campaignToEdit.tts_type || "",
                timeout: campaignToEdit.timeout || 0,
                dispositions: campaignToEdit.dispositions || [],
                call_transfer: Boolean(campaignToEdit.call_transfer),
                call_transfer_number: campaignToEdit.call_transfer_number || "",

            });
        } else {
            reset();
        }
    }, [campaignToEdit, isOpen, reset]);

    useEffect(() => {
        if (filterValue && callNumbersOptions[filterValue]) {
            setAvailableNumbers(callNumbersOptions[filterValue]);
        } else {
            setAvailableNumbers([]);
        }
    }, [filterValue, callNumbersOptions]);

    // Format time to HH:mm:ss
    const formatTime = (timeStr) => (timeStr ? `${timeStr}:00` : "");

    const onSubmit = (data) => {
        const payload = {
            campaign_name: data.campaign_name,
            campaign_description: data.campaign_description,
            company_id: companyId,
            start_time: formatTime(data.start_time),
            end_time: formatTime(data.end_time),
            status: data.status,
            script: data.script,
            dialer: data.dialer,
            numbers: data.numbers,
            stt_type: data.stt_type,
            llm_type: data.llm_type,
            tts_type: data.tts_type,
            timeout: Number(data.timeout),
            language_mode: data.language_mode,
            language:
                data.language_mode === "default"
                    ? languageOptions?.default_language?.[0]?.value
                    : data.language,
            dispositions: data.dispositions,
            call_transfer: Boolean(data.call_transfer),
            call_transfer_number: data.call_transfer ? data.call_transfer_number : "",
            call_transfer_threshold:Number(data.call_transfer_threshold),
            service_id: data.dialer === "Teckinfo" ? data.service_id : "",
        };

        onSave(payload);
        onClose();
    };
    const isMobile = window.innerWidth < 768;

    const rowStyle = {
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: "16px",
        marginBottom: "12px",
    };

    const fieldStyle = {
        display: "flex",
        flexDirection: "column",
    };


    if (!isOpen) return null;

    return (
        <div className="add-subparam-overlay">
            <div className="add-subparam-modal full-width">
                {/* Header */}
                <div className="add-subparam-header">
                    <h3>
                        <Calendar size={18} style={{ marginRight: "6px" }} />
                        {campaignToEdit ? "Edit Campaign" : "Create New Campaign"}
                    </h3>
                    <button onClick={onClose} className="close-btn">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}

                <form id="campaignForm" onSubmit={handleSubmit(onSubmit)} className="add-subparam-body">
                    {/* campaign Name */}

                    <div style={rowStyle}>
                        <div style={fieldStyle}>
                            <label>Campaign ID</label>
                            <input
                                type="text"
                                name="id"
                                className="form-control"
                                value={campaignToEdit?.campaign_id || ""}
                                disabled={true}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <label>Campaign Name</label>
                            <Controller
                                name="campaign_name"
                                control={control}
                                rules={{ required: "Campaign name is required" }}
                                render={({ field }) => (
                                    <input {...field} className={`form-control ${errors.campaign_name ? "error" : ""}`} />
                                )}
                            />
                            {errors.campaign_name && <p className="error-text">{errors.campaign_name.message}</p>}
                        </div>
                        <div style={fieldStyle}>
                            <label>Description</label>
                            <Controller
                                name="campaign_description"
                                control={control}
                                render={({ field }) => <textarea {...field} className="form-control" rows={3} />}
                            />

                        </div>
                        <div style={fieldStyle}>
                            <label>Start Time</label>
                            <Controller
                                name="start_time"
                                control={control}
                                rules={{ required: "Start time is required" }}
                                render={({ field }) => <input type="time" {...field} className="form-control" />}
                            />
                            {errors.start_time && <p className="error-text">{errors.start_time.message}</p>}
                        </div>
                        <div style={fieldStyle}>
                            <label>End Time</label>
                            <Controller
                                name="end_time"
                                control={control}
                                rules={{
                                    required: "End time is required",
                                    validate: (v) => v >= watch("start_time") || "End must be after start",
                                }}
                                render={({ field }) => <input type="time" {...field} className="form-control" />}
                            />
                            {errors.end_time && <p className="error-text">{errors.end_time.message}</p>}
                        </div>
                        <div style={fieldStyle}>
                            <label>Status</label>
                            <Controller
                                name="status"
                                control={control}
                                render={({ field }) => (
                                    <select {...field} className="form-control">
                                        <option value="">Select Status</option>
                                        {statusOptions?.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <label><Globe size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />Language Mode</label>

                            <Controller
                                name="language_mode"
                                control={control}
                                render={({ field }) => (
                                    <div className="flex gap-3" style={{ display: "flex", gap: "20px" }}>
                                        {/* DEFAULT LANGUAGE */}
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                value="default"
                                                checked={field.value === "default"}
                                                onChange={() => {
                                                    field.onChange("default");
                                                    setLanguageMode("default");
                                                    setSelectedLanguage(languageOptions.default_language?.[0]?.value || "");
                                                }}
                                            />
                                            {languageOptions.default_language?.[0]?.label || "English"} (Default)
                                        </label>

                                        {/* CUSTOM LANGUAGE */}
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                value="custom"
                                                checked={field.value === "custom"}
                                                onChange={() => field.onChange("custom")}
                                            />
                                            Select Other Language
                                        </label>
                                    </div>
                                )}
                            />

                            {/* SHOW DROPDOWN ONLY IF custom selected */}
                            {watch("language_mode") === "custom" && (
                                <Controller
                                    name="language"
                                    control={control}
                                    rules={{ required: "Please select language" }}
                                    render={({ field }) => (
                                        <select
                                            {...field}
                                            className="form-control"
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                                setSelectedLanguage(e.target.value);
                                            }}
                                        >
                                            <option value="">Select Language</option>
                                            {languageOptions.other_languages?.map(({ value, label }) => (
                                                <option key={value} value={value}>
                                                    {label}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                />
                            )}
                        </div>


                        <div style={fieldStyle}>

                            <label>  <MessageSquare size={16} /> STT Provider</label>
                            <Controller
                                name="stt_type"
                                control={control}
                                render={({ field }) => (
                                    <select {...field} className="form-control">
                                        <option value="">Select STT</option>
                                        {ivrDropdowns.stt_options?.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            />
                        </div>

                        <div style={fieldStyle}>
                            <label>  <Mic size={16} /> TTS Provider</label>
                            <Controller
                                name="tts_type"
                                control={control}
                                render={({ field }) => (
                                    <select {...field} className="form-control">
                                        <option value="">Select TTS</option>
                                        {ivrDropdowns.tts_options?.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <label><Brain size={16} /> LLM Provider</label>
                            <Controller
                                name="llm_type"
                                control={control}
                                render={({ field }) => (
                                    <select {...field} className="form-control">
                                        <option value="">Select LLM</option>
                                        {ivrDropdowns.llm_options?.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            />
                        </div>
                        <div style={fieldStyle}>
                            <label> <FileText size={16} /> Script</label>
                            <Controller
                                name="script"
                                control={control}
                                render={({ field }) => (
                                    <select {...field} className="form-control">
                                        <option value="">Select Script</option>
                                        {ivrDropdowns.script_options?.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            />
                        </div>
                        <div style={fieldStyle}>
                            {/* Dialer Type */}
                            <label>Dialer Type</label>
                            <Controller
                                name="dialer"
                                control={control}
                                render={({ field }) => (
                                    <select
                                        {...field}
                                        className="form-control"
                                        onChange={(e) => {
                                            const selectedValue = e.target.value;
                                            field.onChange(selectedValue);
                                            setFilterValue(selectedValue);
                                        }}
                                    >
                                        <option value="">Select Dialer</option>
                                        {ivrDropdowns.dialer_options?.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            />
                        </div>
                        {watch("dialer") === "Teckinfo" && (
                            <div style={fieldStyle}>
                                <label>Service ID</label>
                                <Controller
                                    name="service_id"
                                    control={control}
                                    rules={{ required: "Service ID is required when Teckinfo dialer is selected" }}
                                    render={({ field }) => (
                                        <input
                                            type="text"
                                            {...field}
                                            className={`form-control ${errors.service_id ? "error" : ""}`}
                                            placeholder="Enter service ID"
                                        />
                                    )}
                                />
                                {errors.service_id && <p className="error-text">{errors.service_id.message}</p>}
                            </div>
                        )}
                        <div style={fieldStyle}>
                            {/* Phone Numbers */}
                            <label className="mt-2">Phone Numbers</label>
                            <Controller
                                name="numbers"
                                control={control}
                                render={({ field }) => (
                                    <select
                                        {...field}
                                        multiple
                                        className="form-control"
                                        value={field.value || []}
                                        onChange={(e) => {
                                            const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                                            field.onChange(selected);
                                        }}
                                        style={{ height: "120px" }}
                                    >
                                        {availableNumbers.map((num) => (
                                            <option key={num.value} value={num.value}>
                                                {num.label}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            />
                            <small>Hold Ctrl/Cmd to select multiple</small>
                            </div>
                            <div style={fieldStyle}>
                                <label style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                                    <Controller
                                        name="call_transfer"
                                        control={control}
                                        render={({ field }) => (
                                            <ToggleSwitch
                                                checked={Boolean(field.value)}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    field.onChange(checked);
                                                    if (!checked) {
                                                        setValue("call_transfer_number", "");
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                    <PhoneForwarded size={16} />
                                    Enable Call Transfer
                                </label>
                                {watch("call_transfer") && (
                                <div style={fieldStyle}>
                                    <label>
                                        <PhoneForwarded size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />
                                        Call Transfer Number
                                    </label>
                                    <Controller
                                        name="call_transfer_number"
                                        control={control}
                                        rules={{ required: "Call transfer number is required" }}
                                        render={({ field }) => (
                                            <input
                                                type="text"
                                                {...field}
                                                className={`form-control ${errors.call_transfer_number ? "error" : ""}`}
                                                placeholder="Enter transfer number"
                                            />
                                        )}
                                    />
                                    {errors.call_transfer_number && (
                                        <p className="error-text">{errors.call_transfer_number.message}</p>
                                    )}

                                    <label style={{ marginTop: "12px" }}>Call Transfer Threshold</label>
                                    <Controller
                                        name="call_transfer_threshold"
                                        control={control}
                                        rules={{
                                            min: { value: 0, message: "Must be >= 0" },
                                        }}
                                        render={({ field }) => (
                                            <input
                                                type="text"
                                                value={field.value || 0}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                className={`form-control ${errors.call_transfer_threshold ? "error" : ""}`}
                                            />
                                        )}
                                    />
                                    {errors.call_transfer_threshold && (
                                        <p className="error-text">{errors.call_transfer_threshold.message}</p>
                                    )}

                                </div>
                            )}

                            </div>
                            
                            {campaignToEdit && (
                                <div style={fieldStyle}>
                                    <label>Timeout (seconds)</label>
                                    <Controller
                                        name="timeout"
                                        control={control}
                                        render={({ field }) => <input type="number" {...field} className="form-control" min="0" />}
                                    />
                                </div>

                            )}
                            <div style={fieldStyle}>
                                        <label>Dispositions</label>

                                        <Controller
                                            name="dispositions"
                                            control={control}
                                            rules={{ required: "Please select at least one disposition" }}
                                            render={({ field }) => (
                                            <select
                                                {...field}
                                                multiple
                                                className="form-control"
                                                style={{
                                                height: "120px",
                                                }}
                                                value={field.value || []}
                                                onChange={(e) => {
                                                const selectedValues = Array.from(
                                                    e.target.selectedOptions,
                                                    (opt) => opt.value
                                                );
                                                field.onChange(selectedValues);
                                                }}
                                            >
                                                {dispositionOptions?.map((disp) => (
                                                <option key={disp.value} value={disp.value}>
                                                    {disp.label}
                                                </option>
                                                ))}
                                            </select>
                                            )}
                                        />

                                        {errors.dispositions && (
                                            <small className="error-text">
                                            {errors.dispositions.message}
                                            </small>
                                        )}

                                        <small>Hold Ctrl / Cmd to select multiple</small>
                                        </div>

                       
                    </div>
                </form>

                <div className="add-subparam-footer">
                    <button type="button" className="popup-cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="submit" form="campaignForm" className="popup-add-btn">
                        {campaignToEdit ? "Update Campaign" : "Save Campaign"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddCampaignModal;
